/**
 * Client mirror of constants/activityAnatomy.js — how an activity is authored.
 *
 * FUTURE: live-editable presets (admin UI); require specific earlier items;
 * completed-vs-graded so a learner can advance before a teacher scores.
 *
 * Row shape: body is a parts array. content is authored extras (terms, decks).
 * metadata is type behavior (hasQuestions, requireKnowledge, assignmentPoints,
 * pointsAward, studentMayMessage, board, includes). includes is only due / attempts / time limit — not every
 * toggle. continue_settings is the leave-the-step gate. submission_settings is
 * paper visibility after submit, not chat. Labels live on the type spec.
 *
 * Missing keys resolve from the type spec. Do not backfill every activity
 * when a flag is added — see constants/activityAnatomy.js and FUTURE-TASKS.md
 * (Activity settings) for per-type tables and a future DB backfill.
 */

import {
  deckHasSource,
  normalizeSlideDecks,
} from "@/lib/slideDecks";
import { questionIncompleteReasons, type QuestionCompletenessInput } from "@/lib/questionScoring";

export const HOUR_BUCKETS = [
  { value: "theory", label: "Theory" },
  { value: "lab", label: "Lab" },
  { value: "field", label: "Field Experience" },
  { value: "concentration", label: "Concentration" },
  { value: "capstone", label: "Capstone" },
] as const;

export type HourBucket = (typeof HOUR_BUCKETS)[number]["value"];

const HOUR_BUCKET_IDS = new Set<string>(HOUR_BUCKETS.map((b) => b.value));

const HOUR_BUCKET_ALIASES: Record<string, HourBucket> = {
  lecture: "theory",
  lectures: "theory",
  classroom: "theory",
  didactic: "theory",
  instruction: "theory",
  reading: "theory",
  laboratory: "lab",
  labs: "lab",
  practical: "lab",
  practice: "lab",
  hands_on: "lab",
  field_experience: "field",
  fieldwork: "field",
  field_work: "field",
  clinical: "field",
  internship: "field",
};

/** Map a free-form label onto a stored hour bucket. Unknown → theory. */
export function normalizeHourBucket(raw: unknown, fallback: HourBucket = "theory"): HourBucket {
  const text = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (!text) return fallback;
  if (HOUR_BUCKET_IDS.has(text)) return text as HourBucket;
  return HOUR_BUCKET_ALIASES[text] || fallback;
}

export const SETTING_KEYS = [
  "required",
  "requirePrevious",
  "dueAt",
  "timeLimitMinutes",
  "maxAttempts",
] as const;

export type SettingKey = (typeof SETTING_KEYS)[number];

export const SETTING_META: Record<SettingKey, { label: string; hint?: string }> = {
  required: { label: "Required", hint: "Learners must complete this item. Off = optional." },
  requirePrevious: {
    label: "Require previous item",
    hint: "Learner must finish the previous reading or lesson item first. Homework, quizzes, exams, and discussions do not lock what comes after them.",
  },
  dueAt: { label: "Due date" },
  timeLimitMinutes: { label: "Time limit (min)" },
  maxAttempts: { label: "Max attempts", hint: "Empty means unlimited. Quizzes, homework, and exams start at 1." },
};

export const INCLUDE_SETTING_KEYS = ["dueAt", "timeLimitMinutes", "maxAttempts"] as const;

export type IncludeSettingKey = (typeof INCLUDE_SETTING_KEYS)[number];

export const INCLUDE_SETTING_META: Record<IncludeSettingKey, { label: string }> = {
  dueAt: { label: "Due date" },
  timeLimitMinutes: { label: "Time limit" },
  maxAttempts: { label: "Max attempts" },
};

export type ActivityIncludes = Record<IncludeSettingKey, boolean>;

export type HtmlPart = { type: "html"; html: string };
export type MediaSize = {
  width?: number;
  height?: number;
  /** Default true. Off lets width and height diverge. */
  lockScale?: boolean;
};
export type ImagePart = { type: "image"; url: string; alt?: string; mediaId?: string } & MediaSize;
export type VideoPart = { type: "video"; url: string; mediaId?: string } & MediaSize;
export type AudioPart = { type: "audio"; url: string; mediaId?: string };
export type FilePart = { type: "file"; url: string; name: string; mime?: string; mediaId?: string };
export type BodyPart = HtmlPart | ImagePart | VideoPart | AudioPart | FilePart;

const MEDIA_PX_MAX = 4000;

export function sanitizeMediaPx(raw: unknown): number | undefined {
  const value = typeof raw === "number" ? raw : Number(String(raw || "").trim());
  if (!Number.isFinite(value)) return undefined;
  const px = Math.round(value);
  if (px < 1 || px > MEDIA_PX_MAX) return undefined;
  return px;
}

export function mediaSizeFrom(raw: Record<string, unknown> | MediaSize | null | undefined): MediaSize {
  if (!raw) return {};
  const width = sanitizeMediaPx(raw.width);
  const height = sanitizeMediaPx(raw.height);
  const next: MediaSize = {};
  if (width) next.width = width;
  if (height) next.height = height;
  if (raw.lockScale === false) next.lockScale = false;
  return next;
}

/** Student + preview: authored px, shrink to the column on mobile. */
export function mediaBoxStyle(
  size: MediaSize | null | undefined,
  extras?: { defaultRatio?: string },
): { width?: number | string; maxWidth: "100%"; height?: "auto"; aspectRatio?: string; objectFit?: "fill" } {
  const width = size?.width;
  const height = size?.height;
  const locked = size?.lockScale !== false;
  const style: {
    width?: number | string;
    maxWidth: "100%";
    height?: "auto";
    aspectRatio?: string;
    objectFit?: "fill";
  } = { maxWidth: "100%" };
  if (width) style.width = width;
  if (width && height) {
    style.aspectRatio = `${width} / ${height}`;
    style.height = "auto";
    if (!locked) style.objectFit = "fill";
  } else if (height && !width) {
    style.width = "100%";
    style.aspectRatio = extras?.defaultRatio || "16 / 9";
  } else if (extras?.defaultRatio) {
    style.aspectRatio = extras.defaultRatio;
  }
  return style;
}

export type ActivitySection = {
  id: string;
  label: string;
  required?: boolean;
  placeholder?: string | null;
  rows?: number;
};

export type SettingSpec = { visible: boolean; default: unknown };

export type ActivitySpec = {
  sections: ActivitySection[];
  media: false | "video" | "audio";
  special: "slides" | "key_terms" | "discussion" | null;
  questions: boolean;
  settings: Record<SettingKey, SettingSpec>;
};

const CONTENT_SETTINGS: Record<SettingKey, SettingSpec> = {
  required: { visible: false, default: true },
  requirePrevious: { visible: false, default: true },
  dueAt: { visible: false, default: null },
  timeLimitMinutes: { visible: false, default: 0 },
  maxAttempts: { visible: false, default: null },
};

const questionSettings = (_points: number, attempts: number): Record<SettingKey, SettingSpec> => ({
  required: { visible: false, default: true },
  requirePrevious: { visible: false, default: true },
  dueAt: { visible: true, default: null },
  timeLimitMinutes: { visible: false, default: 0 },
  maxAttempts: { visible: true, default: attempts },
});

const gradedSettings = (_points: number): Record<SettingKey, SettingSpec> => ({
  required: { visible: false, default: true },
  requirePrevious: { visible: false, default: true },
  dueAt: { visible: true, default: null },
  timeLimitMinutes: { visible: false, default: 0 },
  maxAttempts: { visible: false, default: null },
});

function section(
  id: string,
  label: string,
  extras: { required?: boolean; rows?: number; placeholder?: string } = {},
): ActivitySection {
  return {
    id,
    label,
    required: extras.required !== false,
    rows: extras.rows || 4,
    placeholder: extras.placeholder || null,
  };
}

export const ACTIVITY_SPECS: Record<string, ActivitySpec> = {
  reading: {
    sections: [section("instructions", "Reading content", { rows: 14 })],
    media: false,
    special: null,
    questions: false,
    settings: { ...CONTENT_SETTINGS },
  },
  audio: {
    sections: [section("instructions", "Description / instructions")],
    media: "audio",
    special: null,
    questions: false,
    settings: { ...CONTENT_SETTINGS },
  },
  video: {
    sections: [section("instructions", "Description / instructions")],
    media: "video",
    special: null,
    questions: false,
    settings: { ...CONTENT_SETTINGS },
  },
  slides: {
    sections: [section("instructions", "Description / instructions")],
    media: false,
    special: "slides",
    questions: false,
    settings: { ...CONTENT_SETTINGS },
  },
  key_terms: {
    sections: [section("instructions", "Notes")],
    media: false,
    special: "key_terms",
    questions: false,
    settings: { ...CONTENT_SETTINGS },
  },
  assessment: {
    sections: [section("instructions", "Description / instructions")],
    media: false,
    special: null,
    questions: true,
    settings: questionSettings(20, 1),
  },
  exam: {
    sections: [section("instructions", "Description / instructions")],
    media: false,
    special: null,
    questions: true,
    settings: questionSettings(100, 1),
  },
  homework: {
    sections: [section("instructions", "Instructions")],
    media: false,
    special: null,
    questions: true,
    settings: questionSettings(10, 1),
  },
  project: {
    sections: [
      section("instructions", "Instructions and materials", {
        placeholder: "Directions and what students need…",
      }),
    ],
    media: false,
    special: null,
    questions: false,
    settings: gradedSettings(10),
  },
  lab: {
    sections: [
      section("instructions", "Instructions and materials", {
        placeholder: "Directions, equipment, and materials…",
      }),
    ],
    media: false,
    special: null,
    questions: false,
    settings: gradedSettings(10),
  },
  discussion: {
    sections: [
      section("instructions", "Discussion prompt", {
        placeholder: "An open-ended prompt students respond to and discuss…",
      }),
    ],
    media: false,
    special: "discussion",
    questions: false,
    settings: gradedSettings(10),
  },
};

export const DISCUSSION_BOARD_KEYS = [
  "allowPromptResponse",
  "allowReplyToOthers",
  "requirePromptBeforeViewing",
] as const;

export type DiscussionBoardKey = (typeof DISCUSSION_BOARD_KEYS)[number];

export type DiscussionBoard = Record<DiscussionBoardKey, boolean>;

export const DISCUSSION_BOARD_META: Record<DiscussionBoardKey, { label: string }> = {
  allowPromptResponse: { label: "Allow responses to the prompt" },
  allowReplyToOthers: { label: "Allow responses to other responses" },
  requirePromptBeforeViewing: {
    label: "Require a prompt response before viewing other responses",
  },
};

export const DISCUSSION_BOARD_DEFAULTS: DiscussionBoard = {
  allowPromptResponse: true,
  allowReplyToOthers: true,
  requirePromptBeforeViewing: true,
};

export function defaultDiscussionBoard(): DiscussionBoard {
  return { ...DISCUSSION_BOARD_DEFAULTS };
}

export function normalizeDiscussionBoard(raw: unknown): DiscussionBoard {
  const next = defaultDiscussionBoard();
  if (!raw || typeof raw !== "object") return next;
  const src = raw as Record<string, unknown>;
  for (const key of DISCUSSION_BOARD_KEYS) {
    if (src[key] != null) next[key] = src[key] !== false;
  }
  return next;
}

/** Turn-in work: does not hold the path; closes when the due date passes. */
export const TURN_IN_TYPES = new Set([
  "homework",
  "assessment",
  "exam",
  "project",
  "lab",
  "discussion",
]);

/** Project and lab: the student hands in files, the teacher marks them. */
export const DROPBOX_TYPES = new Set(["project", "lab"]);

export function isDropboxType(type: string) {
  return DROPBOX_TYPES.has(String(type || ""));
}

export function typeGatesPath(type: string) {
  return !TURN_IN_TYPES.has(String(type || ""));
}

export function typeLocksOnDue(type: string) {
  return TURN_IN_TYPES.has(String(type || ""));
}

export function dueAtMs(activity: { due_at?: unknown; dueAt?: unknown } | null | undefined) {
  const raw = activity?.due_at ?? activity?.dueAt;
  if (!raw) return null;
  const t = new Date(String(raw)).getTime();
  return Number.isFinite(t) ? t : null;
}

export function isDueClosed(
  activity: { type?: string; due_at?: unknown; dueAt?: unknown } | null | undefined,
  extras: { allowLate?: boolean; now?: number } = {},
) {
  if (!typeLocksOnDue(String(activity?.type || ""))) return false;
  if (extras.allowLate === true) return false;
  const due = dueAtMs(activity);
  if (due == null) return false;
  const now = extras.now != null ? Number(extras.now) : Date.now();
  return now > due;
}

/** Points for a no-questions activity. Questions own their own points. 0 = not graded. */
export function assignmentPointsOf(
  type: string,
  metadata?: { assignmentPoints?: unknown; hasQuestions?: unknown } | null,
) {
  if (activityHasQuestions(type, metadata)) return null;
  if (metadata && metadata.assignmentPoints != null && metadata.assignmentPoints !== "") {
    const n = Number(metadata.assignmentPoints);
    if (Number.isInteger(n) && n >= 0) return n;
  }
  if (DROPBOX_TYPES.has(String(type || ""))) return 10;
  return null;
}

export const POINTS_AWARD_ON_COMPLETE = "on_complete";
export const POINTS_AWARD_MANUAL = "manual";

export type PointsAward = typeof POINTS_AWARD_ON_COMPLETE | typeof POINTS_AWARD_MANUAL;

/** Reading, video, audio, slides, and key terms. Turn-in types stay manual. */
const AUTO_AWARD_TYPES = new Set(["reading", "audio", "video", "slides", "key_terms"]);

export function defaultPointsAward(type: string): PointsAward {
  return AUTO_AWARD_TYPES.has(String(type || "")) ? POINTS_AWARD_ON_COMPLETE : POINTS_AWARD_MANUAL;
}

/**
 * How a no-questions point total is awarded. Null when questions own the score.
 * A stored total with no key stays manual — that is what those rows do today.
 */
export function pointsAwardOf(
  type: string,
  metadata?: { assignmentPoints?: unknown; hasQuestions?: unknown; pointsAward?: unknown } | null,
): PointsAward | null {
  if (activityHasQuestions(type, metadata)) return null;
  const raw = metadata && typeof metadata === "object" ? metadata.pointsAward : null;
  if (raw === POINTS_AWARD_ON_COMPLETE || raw === POINTS_AWARD_MANUAL) return raw;
  if (metadata && metadata.assignmentPoints != null && metadata.assignmentPoints !== "") {
    return POINTS_AWARD_MANUAL;
  }
  return defaultPointsAward(type);
}

/**
 * Points written when they complete, for an on_complete activity.
 * Null when this activity is not awarded on completion.
 * A missing completionPoints, or one that is not below the total, means all.
 */
export function completionAwardOf(
  type: string,
  metadata?: {
    assignmentPoints?: unknown;
    hasQuestions?: unknown;
    pointsAward?: unknown;
    completionPoints?: unknown;
  } | null,
): number | null {
  if (pointsAwardOf(type, metadata) !== POINTS_AWARD_ON_COMPLETE) return null;
  const possible = assignmentPointsOf(type, metadata);
  if (!possible) return null;
  const raw = metadata && typeof metadata === "object" ? metadata.completionPoints : null;
  if (raw == null || raw === "" || raw === "all") return possible;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n >= possible) return possible;
  return n;
}

export function activitySpec(type: string): ActivitySpec {
  return ACTIVITY_SPECS[type] || ACTIVITY_SPECS.reading;
}

export function activitySections(type: string): ActivitySection[] {
  return activitySpec(type).sections || [];
}

export function defaultIncludes(type: string): ActivityIncludes {
  const spec = activitySpec(type);
  return {
    dueAt: Boolean(spec.settings.dueAt?.visible),
    timeLimitMinutes: Boolean(spec.settings.timeLimitMinutes?.visible),
    maxAttempts: Boolean(spec.settings.maxAttempts?.visible),
  };
}

export function normalizeIncludes(type: string, metadata?: { includes?: unknown } | null): ActivityIncludes {
  const next = defaultIncludes(type);
  const raw = metadata && typeof metadata.includes === "object" && metadata.includes
    ? (metadata.includes as Record<string, unknown>)
    : null;
  if (!raw) return next;
  for (const key of INCLUDE_SETTING_KEYS) {
    if (raw[key] != null) next[key] = raw[key] !== false;
  }
  return next;
}

export function activityIncludes(
  type: string,
  metadata?: { includes?: unknown } | null,
  stored?: { dueAt?: unknown; timeLimitMinutes?: unknown; maxAttempts?: unknown } | null,
): ActivityIncludes {
  const next = normalizeIncludes(type, metadata);
  const hasMap = Boolean(metadata && metadata.includes && typeof metadata.includes === "object");
  if (hasMap) return next;
  if (stored) {
    if (stored.dueAt) next.dueAt = true;
    if (Number(stored.timeLimitMinutes) > 0) next.timeLimitMinutes = true;
    if (stored.maxAttempts != null) next.maxAttempts = true;
  }
  return next;
}

export function visibleSettings(type: string, includes?: ActivityIncludes | null): IncludeSettingKey[] {
  const on = includes || defaultIncludes(type);
  return INCLUDE_SETTING_KEYS.filter((key) => on[key]);
}

/** Video/audio items need one filled part of that type in body. Not a UI lock. */
export function requiredMediaPart(type: string): "video" | "audio" | null {
  const media = activitySpec(type).media;
  return media === "video" || media === "audio" ? media : null;
}

export function bodyHasRequiredMedia(type: string, parts: BodyPart[]) {
  const need = requiredMediaPart(type);
  if (!need) return true;
  return parts.some((part) => part.type === need && Boolean(String(part.url || "").trim()));
}

/** Why the outline still shows Incomplete. Empty = filled in. */
export function incompleteReasons(input: {
  type: string;
  body?: unknown;
  content?: Record<string, unknown> | null;
  metadata?: { hasQuestions?: unknown } | null;
  questionCount?: number;
  questions?: QuestionCompletenessInput[];
}): string[] {
  const spec = activitySpec(input.type);
  const hasQuestions = activityHasQuestions(input.type, input.metadata);
  const questionCount = Number(input.questionCount) || 0;
  const reasons: string[] = [];

  const questionGaps = () => {
    if (questionCount < 1) reasons.push("Add at least one question.");
    if (input.questions) {
      const n = input.questions.filter((q) => questionIncompleteReasons(q).length > 0).length;
      if (n) reasons.push(n === 1 ? "1 question is still incomplete." : `${n} questions are still incomplete.`);
    }
  };

  if (hasQuestions && spec.questions) {
    questionGaps();
    return reasons;
  }

  if (spec.special === "key_terms") {
    const terms = input.content?.terms;
    if (!Array.isArray(terms) || terms.length === 0) reasons.push("Add at least one key term.");
  } else if (spec.special === "slides") {
    const decks = normalizeSlideDecks(input.content?.decks);
    if (!decks.some(deckHasSource)) reasons.push("Add a slide deck (Canva or PDF).");
  } else if (requiredMediaPart(input.type)) {
    const need = requiredMediaPart(input.type);
    if (!bodyHasRequiredMedia(input.type, partsFromBody(input.body))) {
      reasons.push(need === "audio" ? "Add audio." : "Add a video.");
    }
  } else {
    const section = activitySections(input.type)[0];
    if (section && section.required !== false && !partsFromBody(input.body).some(partHasContent)) {
      reasons.push("Add some content.");
    }
  }

  if (hasQuestions) questionGaps();
  return reasons;
}

/** Spec default, unless this activity stored an override on metadata.hasQuestions. */
export function activityHasQuestions(type: string, metadata?: { hasQuestions?: unknown } | null) {
  if (metadata && metadata.hasQuestions != null) return metadata.hasQuestions !== false;
  return Boolean(activitySpec(type).questions);
}

/** Activity settings checkbox. Missing → on. */
export function activityRequireKnowledgeOn(metadata?: { requireKnowledge?: unknown } | null) {
  return metadata?.requireKnowledge !== false;
}

/** There is a knowledge check and it is required to continue. */
export function activityRequiresKnowledge(
  type: string,
  metadata?: { hasQuestions?: unknown; requireKnowledge?: unknown } | null,
) {
  return activityHasQuestions(type, metadata) && activityRequireKnowledgeOn(metadata);
}

export type SubmissionSettings = {
  showAnswerKey: boolean;
  showIncorrect: boolean;
  showTotalPoints: boolean;
  showQuestionPoints: boolean;
};

export function defaultSubmissionSettings(_type?: string): SubmissionSettings {
  return {
    showAnswerKey: false,
    showIncorrect: false,
    showTotalPoints: true,
    showQuestionPoints: true,
  };
}

export function normalizeSubmissionSettings(raw: unknown, _type?: string): SubmissionSettings {
  const src = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  return {
    showAnswerKey: src.showAnswerKey === true,
    showIncorrect: src.showIncorrect === true,
    showTotalPoints: src.showTotalPoints !== false,
    showQuestionPoints: src.showQuestionPoints !== false,
  };
}

function settingsObject(raw: unknown): Record<string, unknown> | null {
  return raw && typeof raw === "object" && !Array.isArray(raw) && Object.keys(raw).length
    ? (raw as Record<string, unknown>)
    : null;
}

function storedFlag(obj: Record<string, unknown> | null | undefined, key: string): boolean | null {
  if (!obj || !Object.prototype.hasOwnProperty.call(obj, key)) return null;
  return obj[key] === true;
}

/** Learner may send in the work chat. Missing → on, except exam / assessment. */
export function studentMayMessageDefault(type?: string) {
  const t = String(type || "");
  return t !== "exam" && t !== "assessment";
}

/**
 * Authored chat send permission. metadata.studentMayMessage first; leftover
 * submission_settings.studentMayReply second; then the type default.
 */
export function activityStudentMayMessage(
  type: string,
  metadata?: { studentMayMessage?: unknown; submissionSettings?: unknown } | null,
  row?: { submissionSettings?: unknown; submission_settings?: unknown } | null,
) {
  const meta = metadata && typeof metadata === "object" ? (metadata as Record<string, unknown>) : {};
  const fromMeta = storedFlag(meta, "studentMayMessage");
  if (fromMeta != null) return fromMeta;
  const leftover =
    settingsObject(row?.submission_settings ?? row?.submissionSettings) ||
    settingsObject(meta.submissionSettings);
  const fromOld = storedFlag(leftover, "studentMayReply");
  if (fromOld != null) return fromOld;
  return studentMayMessageDefault(type);
}

/** Column first, leftover metadata.submissionSettings second, then defaults. */
export function resolveSubmissionSettings(row: {
  type?: string;
  submissionSettings?: unknown;
  submission_settings?: unknown;
  metadata?: { submissionSettings?: unknown } | null;
} | null | undefined): SubmissionSettings {
  const col = settingsObject(row?.submission_settings ?? row?.submissionSettings);
  const meta = settingsObject(row?.metadata?.submissionSettings);
  return normalizeSubmissionSettings(col || meta, row?.type);
}

export type ContinueDiscussion = {
  replyToPrompt: boolean;
  replyToOthers: number;
  /** Always true. Empty board: the learner's own post counts toward others. */
  countOwnIfNoOthers: boolean;
};

export type ContinueSettings = {
  markComplete: boolean;
  minDwellMinutes: number;
  requireWatch: boolean;
  submitQuestions: boolean;
  requirePass: boolean;
  minPercent: number;
  discussion: ContinueDiscussion;
};

export type ContinueAction = "complete" | "submit" | "discussion";

function clampInt(value: unknown, min: number, max: number, fallback: number) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const whole = Math.round(n);
  if (whole < min) return min;
  if (whole > max) return max;
  return whole;
}

export function defaultContinueSettings(
  type: string,
  extras: { hasQuestions?: boolean; requireKnowledge?: boolean } = {},
): ContinueSettings {
  const spec = activitySpec(type);
  const hasQuestions = extras.hasQuestions != null ? extras.hasQuestions !== false : Boolean(spec.questions);
  const requireKnowledge = extras.requireKnowledge !== false;
  const discussion = spec.special === "discussion";
  return {
    markComplete: true,
    minDwellMinutes: 0,
    requireWatch: String(type || "") === "video",
    submitQuestions: hasQuestions && requireKnowledge,
    requirePass: false,
    minPercent: 70,
    discussion: {
      replyToPrompt: discussion,
      replyToOthers: 0,
      countOwnIfNoOthers: true,
    },
  };
}

export function normalizeContinueSettings(
  raw: unknown,
  type: string,
  extras: { hasQuestions?: boolean; requireKnowledge?: boolean } = {},
): ContinueSettings {
  const base = defaultContinueSettings(type, extras);
  const src = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const disc = src.discussion && typeof src.discussion === "object" ? (src.discussion as Record<string, unknown>) : {};
  const hasQuestions = extras.hasQuestions != null ? extras.hasQuestions !== false : Boolean(activitySpec(type).questions);
  const requireKnowledge = extras.requireKnowledge !== false;
  return {
    markComplete: true,
    minDwellMinutes: clampInt(src.minDwellMinutes, 0, 24 * 60, base.minDwellMinutes),
    requireWatch: src.requireWatch != null ? src.requireWatch === true : base.requireWatch,
    submitQuestions: hasQuestions && requireKnowledge,
    requirePass: hasQuestions && requireKnowledge && src.requirePass === true,
    minPercent: clampInt(src.minPercent, 0, 100, base.minPercent),
    discussion: {
      replyToPrompt: disc.replyToPrompt != null ? disc.replyToPrompt === true : base.discussion.replyToPrompt,
      replyToOthers: clampInt(disc.replyToOthers, 0, 50, base.discussion.replyToOthers),
      countOwnIfNoOthers: true,
    },
  };
}

export function continueAction(_settings?: ContinueSettings | null): ContinueAction {
  return "complete";
}

export function continuePrerequisites(
  settings: ContinueSettings | null | undefined,
  extras: { hasQuestions?: boolean; requireKnowledge?: boolean } = {},
): Array<"questions" | "discussion" | "dwell" | "watch" | "pass"> {
  const required =
    extras.requireKnowledge === false
      ? false
      : extras.hasQuestions === true || Boolean(settings?.submitQuestions);
  const disc = settings?.discussion;
  const out: Array<"questions" | "discussion" | "dwell" | "watch" | "pass"> = [];
  if (required) out.push("questions");
  if (disc?.replyToPrompt || Number(disc?.replyToOthers) > 0) out.push("discussion");
  if (Number(settings?.minDwellMinutes) > 0) out.push("dwell");
  if (settings?.requireWatch) out.push("watch");
  if (required && settings?.requirePass) out.push("pass");
  return out;
}

export function completionModeFor(
  type: string,
  continueSettings?: unknown,
  metadata?: { hasQuestions?: unknown; requireKnowledge?: unknown } | null,
) {
  const hasQuestions = activityHasQuestions(type, metadata);
  const requireKnowledge = activityRequireKnowledgeOn(metadata);
  const settings = normalizeContinueSettings(continueSettings, type, { hasQuestions, requireKnowledge });
  if (hasQuestions && requireKnowledge) return "on_submit" as const;
  if (settings.discussion.replyToPrompt || settings.discussion.replyToOthers > 0) return "on_discussion" as const;
  return "on_complete" as const;
}

export function htmlHasText(html: string | null | undefined) {
  if (!html || !String(html).trim()) return false;
  return Boolean(
    String(html)
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );
}

export function partHasContent(part: BodyPart | null | undefined) {
  if (!part) return false;
  if (part.type === "html") return htmlHasText(part.html);
  return Boolean(String(part.url || "").trim());
}

function coercePart(raw: unknown): BodyPart | null {
  if (!raw || typeof raw !== "object") return null;
  const src = raw as Record<string, unknown>;
  const type = String(src.type || "");
  const mediaId = String(src.mediaId || src.media_id || "").trim() || undefined;
  if (type === "image") {
    const url = String(src.url || "").trim();
    if (!url) return null;
    const alt = String(src.alt || "").trim();
    return {
      type: "image",
      url,
      ...(alt ? { alt } : {}),
      ...(mediaId ? { mediaId } : {}),
      ...mediaSizeFrom(src),
    };
  }
  if (type === "video") {
    const url = String(src.url || "").trim();
    if (!url) return null;
    return {
      type: "video",
      url,
      ...(mediaId ? { mediaId } : {}),
      ...mediaSizeFrom(src),
    };
  }
  if (type === "audio") {
    const url = String(src.url || "").trim();
    if (!url) return null;
    return {
      type: "audio",
      url,
      ...(mediaId ? { mediaId } : {}),
    };
  }
  if (type === "file") {
    const url = String(src.url || "").trim();
    if (!url) return null;
    const name =
      String(src.name || src.filename || "")
        .split(/[\\/]/)
        .pop()
        ?.replace(/[<>]/g, "")
        .trim()
        .slice(0, 200) || "File";
    const mime = String(src.mime || src.mimeType || "").trim();
    return {
      type: "file",
      url,
      name,
      ...(mime ? { mime } : {}),
      ...(mediaId ? { mediaId } : {}),
    };
  }
  const html = src.html != null ? String(src.html) : "";
  if (type === "html" || src.html != null) return { type: "html", html };
  return null;
}

/** Accepts a flat parts array, leftover { id, parts }, or { id, html }. */
export function partsFromBody(body: unknown): BodyPart[] {
  if (!Array.isArray(body)) return [];
  const main: BodyPart[] = [];
  const leftover: BodyPart[] = [];
  for (const block of body) {
    if (!block || typeof block !== "object") continue;
    const src = block as Record<string, unknown>;
    if (Array.isArray(src.parts) || (src.id != null && src.html != null && src.type == null)) {
      const id = String(src.id || "");
      const list = Array.isArray(src.parts)
        ? (src.parts.map(coercePart).filter(Boolean) as BodyPart[])
        : [{ type: "html" as const, html: String(src.html) }];
      if (id === "materials") leftover.push(...list);
      else main.push(...list);
      continue;
    }
    const part = coercePart(src);
    if (part) main.push(part);
  }
  return [...main, ...leftover];
}

export function videoPlayback(url: string): { kind: "youtube" | "vimeo" | "file"; src: string } | null {
  const raw = String(url || "").trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = parsed.pathname.split("/").filter(Boolean)[0];
      return id ? { kind: "youtube", src: `https://www.youtube-nocookie.com/embed/${id}` } : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
      const id =
        parsed.searchParams.get("v") ||
        parsed.pathname.match(/\/(?:embed|shorts)\/([^/]+)/)?.[1];
      return id ? { kind: "youtube", src: `https://www.youtube-nocookie.com/embed/${id}` } : null;
    }
    if (host === "vimeo.com" || host === "player.vimeo.com") {
      const id = parsed.pathname.match(/\/(?:video\/)?(\d+)/)?.[1];
      return id ? { kind: "vimeo", src: `https://player.vimeo.com/video/${id}` } : null;
    }
    return { kind: "file", src: parsed.href };
  } catch {
    return null;
  }
}
